/**
 * RouteErrorIllustration
 *
 * Decorative left-panel background for error / disconnected states (public
 * share notFound / revoked / error, and the app bootstrap failure screen).
 * Concept: "lost route" — a road that breaks off and fades into a dashed
 * dead-end, leading to a disconnected pin (a broken ring with no fill). Shares
 * the map illustration language (grid + Bézier roads + pins) and is fully
 * theme-aware via useMapIllustrationTheme().
 *
 * @module components/common/illustrations/RouteErrorIllustration
 */
import { memo } from 'react';

import { useMapIllustrationTheme } from '@/hooks/useMapIllustrationTheme';

import styles from './RouteErrorIllustration.module.css';

/** Center of the disconnected pin, in viewBox units. */
const CENTER = { x: 250, y: 410 };

/** Disconnected pin geometry. */
const PIN_RING_RADIUS = 44;
const PIN_INNER_RADIUS = 5;

/** Continuous road segments that approach but never reach the center. */
const SOLID_ROADS = ['M-20 360 Q70 410 150 430', 'M250 920 Q235 700 246 560'];

/** Dashed, fading dead-end fragments past the break. */
const BROKEN_ROADS = ['M345 392 Q440 420 520 396', 'M360 470 Q300 520 240 560'];

const BROKEN_RING_OPACITY_DARK = 0.12;
const BROKEN_RING_OPACITY_LIGHT = 0.18;

export const RouteErrorIllustration = memo(function RouteErrorIllustration() {
  const {
    isDark,
    bgColor,
    gridColor,
    gridWidth,
    accent,
    dashColor,
    glowStart,
    glowEnd,
    roadOp,
    prefersReducedMotion,
  } = useMapIllustrationTheme();

  const ringOpacity = isDark
    ? BROKEN_RING_OPACITY_DARK
    : BROKEN_RING_OPACITY_LIGHT;

  return (
    <svg
      viewBox="0 0 500 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className={styles.illustration}
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="errGrid"
          width="50"
          height="50"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 50 0 L 0 0 0 50"
            fill="none"
            stroke={gridColor}
            strokeWidth={gridWidth}
          />
        </pattern>
        {!isDark && (
          <radialGradient id="errGlow" cx="50%" cy="46%" r="55%">
            <stop offset="0%" stopColor={glowStart} />
            <stop offset="100%" stopColor={glowEnd} />
          </radialGradient>
        )}
      </defs>

      <rect
        width="500"
        height="900"
        fill={isDark ? bgColor : 'url(#errGlow)'}
      />
      <rect width="500" height="900" fill="url(#errGrid)" />

      {/* Continuous roads — approach the center */}
      {SOLID_ROADS.map((road) => (
        <g key={road}>
          <path
            d={road}
            stroke={accent}
            strokeWidth="24"
            opacity={roadOp.wide}
            strokeLinecap="round"
          />
          <path
            d={road}
            stroke={dashColor}
            strokeWidth="1.5"
            strokeDasharray="8 14"
            opacity={roadOp.dash}
            strokeLinecap="round"
          />
        </g>
      ))}

      {/* Broken roads — dashed dead-ends past the gap */}
      {BROKEN_ROADS.map((road) => (
        <path
          key={road}
          d={road}
          stroke={accent}
          strokeWidth="3"
          strokeDasharray="14 18"
          opacity={roadOp.edge * 1.4}
          strokeLinecap="round"
        />
      ))}

      {/* Disconnected pin — broken ring, no fill */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={PIN_RING_RADIUS}
        stroke={accent}
        strokeWidth="3"
        strokeDasharray="26 20"
        opacity={ringOpacity}
      >
        {!prefersReducedMotion && (
          <animate
            attributeName="opacity"
            values={`${ringOpacity};${ringOpacity * 0.35};${ringOpacity}`}
            dur="4s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={PIN_INNER_RADIUS}
        fill={accent}
        opacity={ringOpacity * 0.8}
      />
    </svg>
  );
});
